import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Alert, Badge, Button, Col, Collapse, Form, Modal, Row, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import { roleCheck } from "utils/index"
import {
  SURVEYORS,
  MAX_SIZE_UPLOADED_FILE,
  MAX_SIZE_UPLOADED_FILE_TEXT
} from "utils/constants"
import { getCurrentUser, getCurrentRole, getBaseUrl } from "../../../reducers"
import NavBar from "../shared/NavBar"
import {
  getReimbursementLineItems,
  createReimbursementLineItem,
  updateReimbursementLineItem,
  deleteReimbursementLineItem
} from "requesters/reimbursementLineItemsRequester"
import { formatReimbursementTotal, sumReimbursementAmounts } from "utils/reimbursementTotals"

const localTodayYmd = () => {
  const d = new Date()
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0")
  ].join("-")
}

const newDraftLine = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  occurredOn: localTodayYmd(),
  description: "",
  amount: "",
  receipt: null
})

function normalizeReimbursementStatus(raw) {
  if (raw === "submitted") return "pending"
  return raw || "pending"
}

function parseSavedItems(payload) {
  if (!payload?.data) return []
  const list = Array.isArray(payload.data) ? payload.data : [payload.data]
  return list.map(row => {
    const a = row.attributes || {}
    return {
      id: row.id,
      occurredOn: a.occurred_on || a.occurredOn,
      description: a.description,
      amount: a.amount,
      receiptUrl: a.receipt_url || a.receiptUrl,
      createdAt: a.created_at || a.createdAt,
      status: normalizeReimbursementStatus(a.status),
      declinedReason: a.declined_reason || a.declinedReason
    }
  })
}

const MyReimbursements = () => {
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const currentRole = useSelector(getCurrentRole)
  const systemRole = currentUser?.attributes?.role

  const [savedItems, setSavedItems] = useState([])
  const [loadingList, setLoadingList] = useState(true)
  const [draftLines, setDraftLines] = useState([newDraftLine()])
  const [submitting, setSubmitting] = useState(false)
  const [showNewModal, setShowNewModal] = useState(false)
  const [formMountKey, setFormMountKey] = useState(0)
  const [filterDateFrom, setFilterDateFrom] = useState("")
  const [filterDateTo, setFilterDateTo] = useState("")
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editRowId, setEditRowId] = useState(null)
  const [editForm, setEditForm] = useState({
    occurredOn: "",
    description: "",
    amount: "",
    receiptFile: null
  })
  const [savingEdit, setSavingEdit] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const loadSaved = useCallback(() => {
    if (!baseUrl) return
    setLoadingList(true)
    const queryParams = {}
    if (filterDateFrom) queryParams.occurred_on_from = filterDateFrom
    if (filterDateTo) queryParams.occurred_on_to = filterDateTo

    getReimbursementLineItems(baseUrl, queryParams)
      .then(res => {
        if (res.status === 200 && res.response) {
          setSavedItems(parseSavedItems(res.response))
        } else if (res.error) {
          toast.error("Could not load reimbursements.")
        }
      })
      .finally(() => setLoadingList(false))
  }, [baseUrl, filterDateFrom, filterDateTo])

  useEffect(() => {
    loadSaved()
  }, [loadSaved])

  if (!roleCheck(systemRole, currentRole, SURVEYORS)) {
    window.location = "/"
    return null
  }

  const amountTotal = useMemo(() => sumReimbursementAmounts(savedItems), [savedItems])
  const declinedItems = useMemo(
    () => savedItems.filter(item => item.status === "declined"),
    [savedItems]
  )

  const updateDraft = (key, patch) => {
    setDraftLines(lines => lines.map(line => (line.key === key ? { ...line, ...patch } : line)))
  }

  const addDraftLine = () => setDraftLines(lines => [...lines, newDraftLine()])
  const removeDraftLine = key => {
    setDraftLines(lines => {
      if (lines.length <= 1) return lines
      return lines.filter(l => l.key !== key)
    })
  }

  const validateDrafts = () => {
    for (const line of draftLines) {
      if (!line.occurredOn || !line.description.trim() || line.amount === "" || line.amount === null) {
        toast.error("Each line needs a date, description, and amount.")
        return false
      }
      if (!line.receipt) {
        toast.error("Please attach a receipt for each line.")
        return false
      }
      const n = parseFloat(line.amount)
      if (Number.isNaN(n) || n <= 0) {
        toast.error("Amount must be a positive number.")
        return false
      }
      if (line.receipt.size > MAX_SIZE_UPLOADED_FILE) {
        toast.error(`Receipts must be under ${MAX_SIZE_UPLOADED_FILE_TEXT}.`)
        return false
      }
    }
    return true
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!validateDrafts()) return

    setSubmitting(true)
    try {
      for (const line of draftLines) {
        const fd = new FormData()
        fd.append("occurred_on", line.occurredOn)
        fd.append("description", line.description.trim())
        fd.append("amount", String(parseFloat(line.amount)))
        fd.append("receipt", line.receipt)

        const res = await createReimbursementLineItem(baseUrl, fd)
        if (res.error) {
          const errs = res.error.response?.data?.errors
          const msg = Array.isArray(errs)
            ? errs.map(er => er.detail || er.title).filter(Boolean).join(" ")
            : (res.error.message || "Could not save reimbursement.")
          toast.error(msg || "Could not save reimbursement.")
          return
        }
        if (!(res.status >= 200 && res.status < 300)) {
          toast.error("Could not save reimbursement.")
          return
        }
      }
      toast.success("Reimbursement submitted.")
      setDraftLines([newDraftLine()])
      setShowNewModal(false)
      setFormMountKey(k => k + 1)
      loadSaved()
    } finally {
      setSubmitting(false)
    }
  }

  const openNewModal = () => {
    setDraftLines([newDraftLine()])
    setFormMountKey(k => k + 1)
    setShowNewModal(true)
  }

  const closeNewModal = () => {
    setShowNewModal(false)
    setDraftLines([newDraftLine()])
    setFormMountKey(k => k + 1)
  }

  const clearDateFilters = () => {
    setFilterDateFrom("")
    setFilterDateTo("")
  }

  const hasDateFilters = Boolean(filterDateFrom || filterDateTo)

  const openEdit = row => {
    if (!(row.status === "pending" || row.status === "declined")) {
      toast.error("Only pending or declined items can be edited.")
      return
    }

    setEditRowId(row.id)
    setEditForm({
      occurredOn: row.occurredOn || "",
      description: row.description || "",
      amount: row.amount != null ? String(row.amount) : "",
      receiptFile: null
    })
    setShowEditModal(true)
  }

  const closeEdit = () => {
    setShowEditModal(false)
    setEditRowId(null)
    setEditForm({
      occurredOn: "",
      description: "",
      amount: "",
      receiptFile: null
    })
  }

  const saveEdit = async e => {
    e.preventDefault()
    if (!editRowId) return

    if (!editForm.occurredOn || !editForm.description.trim() || editForm.amount === "") {
      toast.error("Date, description, and amount are required.")
      return
    }
    const n = parseFloat(editForm.amount)
    if (Number.isNaN(n) || n <= 0) {
      toast.error("Amount must be a positive number.")
      return
    }
    if (editForm.receiptFile && editForm.receiptFile.size > MAX_SIZE_UPLOADED_FILE) {
      toast.error(`Receipt must be under ${MAX_SIZE_UPLOADED_FILE_TEXT}.`)
      return
    }

    setSavingEdit(true)
    const fd = new FormData()
    fd.append("occurred_on", editForm.occurredOn)
    fd.append("description", editForm.description.trim())
    fd.append("amount", String(n))
    if (editForm.receiptFile) {
      fd.append("receipt", editForm.receiptFile)
    }

    const res = await updateReimbursementLineItem(baseUrl, editRowId, fd)
    setSavingEdit(false)

    if (res.error) {
      const errs = res.error.response?.data?.errors
      const msg = Array.isArray(errs)
        ? errs.map(er => er.detail || er.title).filter(Boolean).join(" ")
        : (res.error.message || "Could not save changes.")
      toast.error(msg || "Could not save changes.")
      return
    }

    toast.success("Reimbursement updated.")
    closeEdit()
    loadSaved()
  }

  const deleteRow = async row => {
    if (!(row.status === "pending" || row.status === "declined")) {
      toast.error("Only pending or declined items can be deleted.")
      return
    }
    const confirmed = window.confirm("Delete this reimbursement line item?")
    if (!confirmed) return

    setDeletingId(row.id)
    const res = await deleteReimbursementLineItem(baseUrl, row.id)
    setDeletingId(null)
    if (res.error) {
      const errs = res.error.response?.data?.errors
      const msg = Array.isArray(errs)
        ? errs.map(er => er.detail || er.title).filter(Boolean).join(" ")
        : (res.error.message || "Could not delete reimbursement.")
      toast.error(msg || "Could not delete reimbursement.")
      return
    }

    toast.success("Reimbursement deleted.")
    loadSaved()
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-3 col-xl-2">
          <NavBar />
        </div>
        <div className="col-md-8 col-xl-10">
          <h1 className="mb-4">My Reimbursements</h1>
          <p className="text-muted mb-4">
            Use <strong>New reimbursement</strong> to add lines. Each line must include a receipt attachment.
          </p>

          <div className="d-flex flex-wrap justify-content-end mb-3">
            <Button type="button" variant="primary" onClick={openNewModal}>
              New reimbursement
            </Button>
          </div>

          <div className="mb-3 d-flex flex-wrap align-items-center">
            <Button
              type="button"
              variant="link"
              className="p-0"
              onClick={() => setFiltersOpen(o => !o)}
              aria-expanded={filtersOpen}
              aria-controls="my-reimbursements-filters">
              {filtersOpen ? "- Hide filters" : "+ Show filters"}
            </Button>
            {hasDateFilters && !filtersOpen && (
              <span className="text-muted small ml-2">Date filter active</span>
            )}
          </div>

          {!loadingList && declinedItems.length > 0 && (
            <Alert variant="warning" className="mb-3">
              <div className="font-weight-bold mb-1">
                {declinedItems.length} reimbursement{declinedItems.length > 1 ? "s were" : " was"} declined
              </div>
              <div className="small">
                {declinedItems.map(item => (
                  <div key={item.id} className="mb-1">
                    {item.occurredOn} - {item.description}: {item.declinedReason || "No reason provided."}
                  </div>
                ))}
              </div>
            </Alert>
          )}

          <Collapse in={filtersOpen}>
            <div id="my-reimbursements-filters" className="mb-4">
              <Form>
                <Row>
                  <Col md={6} lg={4} className="mb-3">
                    <Form.Group controlId="my-reimb-filter-date-from" className="mb-0">
                      <Form.Label>Date from</Form.Label>
                      <Form.Control
                        type="date"
                        value={filterDateFrom}
                        onChange={e => setFilterDateFrom(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} lg={4} className="mb-3">
                    <Form.Group controlId="my-reimb-filter-date-to" className="mb-0">
                      <Form.Label>Date to</Form.Label>
                      <Form.Control
                        type="date"
                        value={filterDateTo}
                        onChange={e => setFilterDateTo(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12} lg={4} className="mb-3 d-flex align-items-end">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={clearDateFilters}
                      disabled={!hasDateFilters}>
                      Clear dates
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          </Collapse>

          {loadingList ? (
            <p>Loading…</p>
          ) : savedItems.length === 0 ? (
            <p className="text-muted mb-4">
              {hasDateFilters ? "No reimbursements in this date range." : "No reimbursements yet."}
            </p>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                <tr>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Receipt</th>
                  <th>Notes</th>
                  <th style={{ minWidth: "140px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {savedItems.map(row => (
                  <tr key={row.id}>
                    <td>
                      <Badge
                        variant={
                          row.status === "closed"
                            ? "info"
                            : row.status === "approved"
                              ? "success"
                              : row.status === "declined"
                                ? "danger"
                                : "warning"
                        }>
                        {row.status}
                      </Badge>
                    </td>
                    <td>{row.occurredOn}</td>
                    <td>{row.description}</td>
                    <td>${row.amount}</td>
                    <td>
                      {row.receiptUrl ? (
                        <a href={row.receiptUrl} target="_blank" rel="noopener noreferrer">
                          View receipt
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{row.status === "declined" && row.declinedReason ? row.declinedReason : "—"}</td>
                    <td>
                      {row.status === "pending" || row.status === "declined" ? (
                        <div className="d-flex flex-wrap">
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            className="mr-1 mb-1"
                            onClick={() => openEdit(row)}>
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            className="mb-1"
                            disabled={deletingId === row.id}
                            onClick={() => deleteRow(row)}>
                            {deletingId === row.id ? "…" : "Delete"}
                          </Button>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </Table>
              <p className="mt-2 mb-4 font-weight-bold" aria-live="polite">
                Total: ${formatReimbursementTotal(amountTotal)}
                {hasDateFilters ? " (filtered)" : ""}
              </p>
            </>
          )}

          <Modal show={showEditModal} onHide={closeEdit} size="lg" centered backdrop="static">
            <Form onSubmit={saveEdit}>
              <Modal.Header closeButton>
                <Modal.Title>Edit reimbursement</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row>
                  <Col md={4} className="mb-3">
                    <Form.Group controlId="edit-occurred-on">
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        required
                        value={editForm.occurredOn}
                        onChange={e => setEditForm(f => ({ ...f, occurredOn: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group controlId="edit-amount">
                      <Form.Label>Amount</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0.01"
                        required
                        value={editForm.amount}
                        onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group controlId="edit-receipt">
                      <Form.Label>Receipt</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*,.pdf"
                        onChange={e => {
                          const file = e.target.files?.[0] || null
                          setEditForm(f => ({ ...f, receiptFile: file }))
                        }}
                      />
                      <Form.Text className="text-muted">
                        Leave empty to keep the current receipt.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group controlId="edit-description">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    required
                    value={editForm.description}
                    onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                  />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button type="button" variant="outline-secondary" onClick={closeEdit} disabled={savingEdit}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={savingEdit}>
                  {savingEdit ? "Saving…" : "Save changes"}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>

          <Modal
            show={showNewModal}
            onHide={closeNewModal}
            size="lg"
            centered
            backdrop="static"
            dialogClassName="reimbursement-modal-dialog"
            contentClassName="d-flex flex-column overflow-hidden">
            <Form
              key={formMountKey}
              onSubmit={handleSubmit}
              className="d-flex flex-column w-100"
              style={{
                maxHeight: "calc(100vh - 2.5rem)",
                minHeight: 0
              }}>
              <Modal.Header closeButton className="flex-shrink-0">
                <Modal.Title>New reimbursement</Modal.Title>
              </Modal.Header>
              <Modal.Body
                className="p-0 d-flex flex-column"
                style={{
                  flex: "1 1 0%",
                  minHeight: 0,
                  overflow: "hidden"
                }}>
                <div
                  className="px-3 py-2"
                  style={{
                    flex: "1 1 0%",
                    minHeight: 0,
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch"
                  }}>
                  {draftLines.map((line, index) => (
                    <div key={line.key} className="border rounded p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="font-weight-bold text-muted">Line {index + 1}</span>
                        {draftLines.length > 1 && (
                          <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => removeDraftLine(line.key)}>
                            Remove line
                          </Button>
                        )}
                      </div>
                      <Row>
                        <Col md={4} className="mb-2">
                          <Form.Group controlId={`modal-date-${line.key}`}>
                            <Form.Label>Date</Form.Label>
                            <Form.Control
                              type="date"
                              required
                              value={line.occurredOn}
                              onChange={e => updateDraft(line.key, { occurredOn: e.target.value })}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Form.Group controlId={`modal-amount-${line.key}`}>
                            <Form.Label>Amount</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              min="0.01"
                              required
                              placeholder="0.00"
                              value={line.amount}
                              onChange={e => updateDraft(line.key, { amount: e.target.value })}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-2">
                          <Form.Group controlId={`modal-receipt-${line.key}`}>
                            <Form.Label>Receipt</Form.Label>
                            <Form.Control
                              type="file"
                              required
                              accept="image/*,.pdf"
                              onChange={e => {
                                const file = e.target.files?.[0] || null
                                updateDraft(line.key, { receipt: file })
                              }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group controlId={`modal-desc-${line.key}`}>
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          required
                          placeholder="What was purchased?"
                          value={line.description}
                          onChange={e => updateDraft(line.key, { description: e.target.value })}
                        />
                      </Form.Group>
                    </div>
                  ))}

                  <Button type="button" variant="outline-primary" className="mr-2 mb-2" onClick={addDraftLine}>
                    Add another line
                  </Button>
                </div>
              </Modal.Body>
              <Modal.Footer className="flex-shrink-0">
                <Button type="button" variant="outline-secondary" onClick={closeNewModal} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? "Submitting…" : "Submit reimbursement"}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </div>
      </div>
    </div>
  )
}

export default MyReimbursements
